import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Target, Users, Award, TrendingUp, Globe, Shield } from 'lucide-react';

const AboutPage = () => {
  const values = [
    { icon: Target, title: 'Quality First', description: 'Committed to delivering only certified, tested industrial raw materials' },
    { icon: Users, title: 'Customer Focus', description: 'Understanding and meeting specific industrial requirements with precision' },
    { icon: Award, title: 'Reliability', description: 'Consistent supply chain and on-time delivery across India' },
    { icon: TrendingUp, title: 'Growth Oriented', description: 'Expanding our product portfolio and supplier network continuously' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">About Voltron Calcium</h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              A Private Limited company dedicated to supplying high-quality industrial chemicals 
              and minerals to manufacturing enterprises across India.
            </p>
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6">Who We Are</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  <strong>Voltron Calcium Pvt Ltd</strong> is a structured, growth-focused industrial 
                  chemicals and minerals trading company registered under the Government of India 
                  (GSTIN: 05AAHCV3224B2ZT).
                </p>
                <p>
                  We specialize in sourcing and supplying critical raw materials to paint, plastic, 
                  rubber, paper, construction, pharmaceutical, and animal feed industries. Our 
                  procurement model is built on vendor qualification, quality assurance, and 
                  reliable logistics.
                </p>
                <p>
                  With offices in Dehradun and Saharanpur, we maintain strategic partnerships with 
                  verified manufacturers and maintain inventory planning capabilities to serve 
                  bulk industrial requirements efficiently.
                </p>
                <p>
                  Our team brings technical expertise in industrial minerals and chemicals, ensuring 
                  that every product meets the specific application requirements of our clients.
                </p>
              </div>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1611581372056-30cf28a7bd2e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxyZWZpbmVyeXxlbnwwfHx8fDE3NzE4NTU2NjN8MA&ixlib=rb-4.1.0&q=85"
                alt="Industrial facility"
                className="rounded-lg shadow-xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Our Core Values</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Principles that guide our operations and partnerships
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
                      <Icon className="w-8 h-8 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{value.title}</h3>
                    <p className="text-gray-600">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Company Details */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-slate-900 mb-12 text-center">Company Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4">
                    <Globe className="w-8 h-8 text-blue-600 mt-1" />
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-4">Registered Office</h3>
                      <p className="text-gray-700 leading-relaxed">
                        402/C2, Third Floor<br />
                        Omega Heights<br />
                        Dehradun, Uttarakhand – 248001<br />
                        India
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4">
                    <Globe className="w-8 h-8 text-blue-600 mt-1" />
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-4">Corporate Office</h3>
                      <p className="text-gray-700 leading-relaxed">
                        F-14, Industrial Estate<br />
                        Delhi Road<br />
                        Saharanpur, Uttar Pradesh – 247001<br />
                        India
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4">
                    <Shield className="w-8 h-8 text-blue-600 mt-1" />
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-4">Company Status</h3>
                      <p className="text-gray-700 leading-relaxed">
                        <strong>Type:</strong> Private Limited Company<br />
                        <strong>GSTIN:</strong> 05AAHCV3224B2ZT
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4">
                    <Target className="w-8 h-8 text-blue-600 mt-1" />
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-4">Business Type</h3>
                      <p className="text-gray-700 leading-relaxed">
                        <strong>Type:</strong> Industrial Chemicals & Minerals Trading Company
                      </p>
                      <p className="text-gray-600 mt-4">
                        <strong>Phone:</strong><br />
                        <a href="tel:+918679475554" className="text-blue-600 hover:underline">
                          +91-86794 75554
                        </a>
                      </p>
                      <p className="text-gray-600 mt-2">
                        <strong>Email:</strong><br />
                        <a href="mailto:sales@voltroncalcium.com" className="text-blue-600 hover:underline">
                          sales@voltroncalcium.com
                        </a>
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="container mx-auto px-6 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold mb-6">Our Mission</h2>
            <p className="text-xl leading-relaxed text-blue-100">
              To be India's most reliable industrial chemicals and minerals supply partner by 
              maintaining the highest standards of quality, transparency, and operational excellence 
              in every transaction.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
